/**
 * The tab spec's preparation: what runs once on the data after RESET and
 * before any table. The chosen saved pipelines and scripts in the order the
 * spec lists them, then the lines that close every preparation. Pure: the
 * sources come in already read. The dashboard's `prepParts` names every part
 * a pipeline; the tab spec names each part by its kind, so a failure says
 * which one it was.
 */

import { weightsLine } from "features/Dashboard/runtime/prepScript"

/** A saved pipeline or console script as the preparation needs it. */
export interface PrepSource {
  name: string
  kind: "pipeline" | "script"
  script: string
}

/** One part of the preparation, run on its own so a failure can name it. */
export interface PreparationPart {
  /** What a failure names: `Pipeline "x"`, `Script "x"` or the line itself. */
  label: string
  script: string
}

/** What is said when the saved pipelines and scripts could not be read. */
export const SOURCES_NOT_LOADED = "Could not load the saved pipelines and scripts."

/** `script` without blank lines before its first line or anything after its last (as the dashboard trims). */
const trimLines = (script: string): string =>
  script.replace(/^(?:[ \t]*\r?\n)+/, "").trimEnd()

/**
 * A line that starts with a command that undoes every part before it:
 * RESET and RECALL (the same command), CLEAR, PROJECT, or `{`, which the
 * engine reads as PROJECT. Any case, after spaces. A line whose first
 * character is `#` is a comment and never matches.
 */
const UNDOING_LINE = /^[ \t]*(?:(RESET|RECALL|CLEAR|PROJECT)\b|\{)/im

/**
 * A line that starts with SETSTART, which moves the point RESET returns to:
 * part-way through a listed script, or in a preparation that RESET is meant
 * to undo. Any case, after spaces; a `#` comment line or a field such as
 * `<<SETSTARTS>>` never matches.
 */
const SETSTART_LINE = /^[ \t]*SETSTART\b/im

/** A name that starts with the prefix the tab spec keeps for its own tables and fields. */
const RESERVED_NAME = /(?:^|[^A-Za-z0-9_])__ts_/i

/**
 * The lines that close every preparation, before the weight line: they clear
 * an exclusion list and a scope a script may have set, so neither reaches
 * the tables.
 */
const CLOSING_LINES = ["EXCL", "SCOPE"]

const labelOf = (source: PrepSource): string =>
  `${source.kind === "script" ? "Script" : "Pipeline"} "${source.name}"`

/**
 * Why `source` cannot be part of the preparation, or null when it can. A
 * check against mistakes, not a parser: a script can still build one of
 * these commands in a way no line shows (a DEFINE macro can hide a RESET).
 */
const refusalOf = (source: PrepSource, text: string): string | null => {
  const label = labelOf(source)
  if (!text) return `${label} is empty or could not be read.`
  const undoing = UNDOING_LINE.exec(text)
  if (undoing) {
    const what = undoing[1] ? `it contains ${undoing[1].toUpperCase()}` : "it starts a new project"
    return `${label} cannot run in the preparation: ${what}, which would undo everything before it.`
  }
  if (SETSTART_LINE.test(text))
    return `${label} cannot run in the preparation: it contains SETSTART, which moves the point RESET returns to.`
  if (RESERVED_NAME.test(text))
    return `${label} cannot run in the preparation: it uses a name reserved for the tab spec.`
  return null
}

/**
 * Why `script` cannot go on the Run on load list, or null when it can: the
 * preparation's rule in Run on load words. The Conductor checks the same
 * rule before it runs a listed script (EndPoints.cpp OnLoadRefusal).
 */
export const runOnLoadRefusal = (script: string): string | null => {
  const text = trimLines(script)
  if (!text) return "It is empty or could not be read."
  const undoing = UNDOING_LINE.exec(text)
  if (undoing) {
    const what = undoing[1] ? `it contains ${undoing[1].toUpperCase()}` : "it starts a new project"
    return `It cannot run on load: ${what}, which would undo everything before it.`
  }
  if (SETSTART_LINE.test(text))
    return "It cannot run on load: it contains SETSTART, which moves the point RESET returns to."
  if (RESERVED_NAME.test(text)) return "It cannot run on load: it uses a name reserved for the tab spec."
  return null
}

/**
 * The preparation in the order it runs: each chosen source's script, then
 * the closing lines (bare `EXCL`, bare `SCOPE`) and the weight line, always,
 * so an exclusion list, a scope or a USEWEIGHTS inside a script never
 * reaches the tables: `USEWEIGHTS <<weight>>` when a weight is set, bare
 * `USEWEIGHTS` otherwise. A chosen name that is neither a saved pipeline nor
 * a saved script, or a chosen source that is empty, undoes the preparation,
 * contains SETSTART or uses a name of the tab spec, gives a `problem` (the first in chosen
 * order) and no part at all. A chosen name in `onLoad` is skipped silently:
 * the Conductor ran it on load, so its variables are already there.
 */
export const preparationParts = (args: {
  sources: PrepSource[]
  chosen: string[]
  weight: string | null
  /** Names the Conductor already ran on load: skipped, so none runs twice. */
  onLoad?: string[]
}): { parts: PreparationPart[]; problem: string | null } => {
  const byName = new Map(args.sources.map((s) => [s.name, s]))
  const ranOnLoad = new Set(args.onLoad ?? [])
  const parts: PreparationPart[] = []
  for (const name of args.chosen) {
    if (ranOnLoad.has(name)) continue
    const source = byName.get(name)
    if (!source)
      return {
        parts: [],
        problem: `"${name}" is not a saved pipeline or script of this project.`,
      }
    const text = trimLines(source.script)
    const problem = refusalOf(source, text)
    if (problem) return { parts: [], problem }
    parts.push({ label: labelOf(source), script: text })
  }
  for (const line of [...CLOSING_LINES, weightsLine(args.weight)])
    parts.push({ label: line, script: line })
  return { parts, problem: null }
}
